import {
  Fragment,
  KeyboardEvent,
  useCallback,
  useEffect,
  useState,
} from 'react';

import { Combobox } from '@headlessui/react';
import { tracking } from '@iwoca/frontend-tracking-library';
import { IconPlus, IconZoomOutline } from '@iwoca/icons';
import { GetCompanySearchResponse } from '@iwoca/lapi-client/edge';
import { Button, Input, Spinner } from '@iwoca/orion';
import { clsx } from 'clsx';
import { useFormContext } from 'react-hook-form';

import styles from './CompanyInputUK.module.css';
import { fetchIwocaGetCompanySearch } from '../../../../api/lendingApi/lendingApiQueries';
import { TCompanyDetailsDataUK } from '../CompanyDetailsUK';

type CompanySearchCompany = GetCompanySearchResponse['data'][number];

const CompanyInputUK = () => {
  const {
    formState: { errors },
    setValue,
    getValues,
  } = useFormContext<TCompanyDetailsDataUK>();

  // Each time the API is called, we add one to this value. This lets us continue to show
  // the spinner until the last call is resolved
  const [numberOfCallsLoading, setNumberOfCallsLoading] = useState(0);
  const [searchQuery, setSearchQuery] = useState('');

  const [showDefaultCompany, setShowDefaultCompany] = useState(
    Boolean(getValues('companyName') && getValues('companyNumber')),
  );

  const [selectedCompany, setSelectedCompany] =
    useState<GetCompanySearchResponse['data'][number]>();

  const [companyOptions, setCompanyOptions] = useState<CompanySearchCompany[]>(
    [],
  );

  useEffect(() => {
    // Search "approve" or "decline" for fake STAGING companies
    const companySearch = async () => {
      setNumberOfCallsLoading((prevNumber) => prevNumber + 1);
      if (searchQuery) {
        try {
          const response = await fetchIwocaGetCompanySearch({
            query: { company_name: searchQuery },
          });

          if (response) {
            setCompanyOptions(response.data);
          }
        } catch {
          setCompanyOptions([]);
        }
      } else {
        setCompanyOptions([]);
        setSelectedCompany(undefined);
      }
      setNumberOfCallsLoading((prevNumber) => prevNumber - 1);
    };

    companySearch();
  }, [searchQuery]);

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && numberOfCallsLoading > 0) {
      e.preventDefault();
    }
  };

  const handleChange = (company: GetCompanySearchResponse['data'][number]) => {
    setSelectedCompany(company);

    setValue('companyName', company.name, {
      shouldValidate: true,
    });

    setValue('companyNumber', company.number, {
      shouldValidate: true,
    });

    setValue('companyType', company.company_type, {
      shouldValidate: true,
    });

    setValue('startDateYear', company.registration_date.split('-')[0], {
      shouldValidate: true,
    });

    setValue('startDateMonth', company.registration_date.split('-')[1], {
      shouldValidate: true,
    });

    tracking.ampli.companyNameSearched();
  };

  const buildDefaultValue = () =>
    getValues('companyName') && getValues('companyNumber')
      ? `${getValues('companyName')} | ${getValues('companyNumber')}`
      : '';

  const renderOptions = useCallback(() => {
    if (numberOfCallsLoading > 0) {
      return (
        <div className={styles.spinnerContainer}>
          <Spinner />
        </div>
      );
    }

    return (
      <>
        {companyOptions && companyOptions.length > 0 ? (
          companyOptions.map((company, index) => (
            <Combobox.Option
              className={({ active }) =>
                clsx(
                  styles.option,
                  {
                    [styles.active]: active,
                  },
                  'dd-privacy-mask',
                )
              }
              key={index}
              value={company}
            >
              {company.name} | {company.number}
            </Combobox.Option>
          ))
        ) : (
          <p className={styles.noResults}>No results</p>
        )}
      </>
    );
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [numberOfCallsLoading]);

  const resetSelectedCompany = () => {
    setSearchQuery('');
    setSelectedCompany(undefined);
    setShowDefaultCompany(false);

    setValue('companyName', '');
    setValue('companyNumber', '');
  };

  return (
    <Combobox onChange={handleChange}>
      <div className={styles.companiesSearch}>
        {selectedCompany || showDefaultCompany ? (
          <>
            <Input
              className={styles.companyInput}
              label="Limited company"
              value={buildDefaultValue()}
              description="Your company must be registered with Companies House to proceed."
              disabled
              error={
                (errors?.companyName?.message && errors.companyName.message) ||
                (errors?.companyNumber?.message && errors.companyNumber.message)
              }
            />
            <Button
              className={styles.clearButton}
              type="reset"
              variant="tertiary"
              size="small"
              data-testid="clear-button-company"
              onClick={resetSelectedCompany}
            >
              <IconPlus
                className={styles.clearButtonIcon}
                height="24"
                width="24"
              />
            </Button>
          </>
        ) : (
          <Combobox.Input
            onChange={(event) => {
              setSearchQuery(event.target.value);
            }}
            as={Fragment}
            displayValue={({ name, number }: CompanySearchCompany) =>
              `${name} | ${number}`
            }
          >
            <Input
              iconRight={!selectedCompany ? <IconZoomOutline /> : undefined}
              label="Limited company"
              description="Your company must be registered with Companies House to proceed."
              placeholder="Search a company name or number"
              className={styles.companyInput}
              onKeyDown={handleKeyDown}
              error={
                (errors?.companyName?.message && errors.companyName.message) ||
                (errors?.companyNumber?.message && errors.companyNumber.message)
              }
            />
          </Combobox.Input>
        )}
        {searchQuery && (
          <Combobox.Options className={styles.options}>
            {renderOptions()}
          </Combobox.Options>
        )}
      </div>
    </Combobox>
  );
};

export default CompanyInputUK;
